import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { useToast } from '../../hooks/use-toast';
import useSEO from '../../components/SEO';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const navigate = useNavigate();
    const { toast } = useToast();

    useSEO({
        title: "Mot de passe oublié - ISTMECH",
        description: "Réinitialisez votre mot de passe.",
        url: "https://www.istmech.edu.ht/forgot-password"
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            await api.post('/auth/forgot-password', { email });
            // Always show success to prevent email enumeration (backend behavior)
            setIsSuccess(true);
            toast({
                title: "Email envoyé",
                description: "Si un compte existe avec cet email, vous recevrez les instructions de réinitialisation.",
            });
        } catch (error) {
            console.error(error);
            toast({
                variant: "destructive",
                title: "Erreur",
                description: "Une erreur est survenue lors de la demande. Veuillez réessayer plus tard.",
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
            <Card className="w-full max-w-md">
                <CardHeader>
                    <CardTitle>Mot de passe oublié ?</CardTitle>
                    <CardDescription>
                        Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {isSuccess ? (
                        <div className="text-center space-y-4 animate-in fade-in slide-in-from-bottom-2">
                            <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto text-green-600 dark:text-green-400 text-3xl">
                                ✉️
                            </div>
                            <h3 className="font-semibold text-lg">Email envoyé !</h3>
                            <p className="text-muted-foreground text-sm">
                                Consultez votre boîte de réception (et vos spams) pour trouver le lien de réinitialisation.
                            </p>
                            <Button variant="outline" className="w-full mt-4" onClick={() => navigate('/login')}>
                                Retour à la connexion
                            </Button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="exemple@istmech.edu.ht"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isLoading}>
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Envoi en cours...
                                    </>
                                ) : (
                                    'Envoyer le lien'
                                )}
                            </Button>
                        </form>
                    )}
                </CardContent>
                {!isSuccess && (
                    <CardFooter className="flex justify-center border-t pt-4">
                        <Button variant="link" size="sm" className="text-muted-foreground" onClick={() => navigate('/login')}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Retour à la connexion
                        </Button>
                    </CardFooter>
                )}
            </Card>
        </div>
    );
};

export default ForgotPassword;
